import { useEffect, useRef, useState } from "react";
import { RouteComponentProps } from "@gatsbyjs/reach-router";
import { APColumn, APExpanded, APRow, APText, APIcon, APCard, APPalette, getIt } from "ap-components";
import PartnerApiServices from "../../Services/PartnerApiServices";

// =============================================================================
// NPS feedback landing page.
//
// Reached from the NPS fallback email, which puts the chosen score in the link
// (`/nps_feedback?token=<jwt>&score=N`). The score is recorded by POSTing from
// here rather than by the link itself — mail scanners prefetch URLs, and a GET
// would file a score the partner never picked.
// =============================================================================

type State =
    | { kind: "submitting" }
    | { kind: "done"; already: boolean; score: number }
    | { kind: "expired" }
    | { kind: "error"; message: string };

function getSearchParam(key: string): string | null {
    return new URLSearchParams(window.location.search).get(key);
}

function parseScore(raw: string | null): number | null {
    if (raw === null || raw.trim() === "") return null;
    const n = Number(raw);
    return Number.isInteger(n) && n >= 0 && n <= 10 ? n : null;
}

// Design shows the rating zero-padded to two digits (09, 07); 10 stays 10.
function formatScore(score: number): string {
    return String(score).padStart(2, "0");
}

export default function NpsFeedbackPage(_props: RouteComponentProps) {
    const [state, setState] = useState<State>({ kind: "submitting" });
    // A remount would re-POST, and the second call comes back alreadyRecorded — showing a
    // duplicate message to someone who answered once.
    const submitted = useRef(false);

    useEffect(() => {
        if (submitted.current) return;
        submitted.current = true;
        submit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function submit() {
        const tkn = getSearchParam("token");
        const score = parseScore(getSearchParam("score"));
        if (!tkn || score === null) {
            setState({ kind: "error", message: "This feedback link is not complete. Please use the link from your email." });
            return;
        }
        try {
            const res = await getIt(PartnerApiServices).submitNpsEmailScore(tkn, score);
            // A stale link whose cycle rolled over records nothing — it must not read as success.
            if (res.expired) {
                setState({ kind: "expired" });
                return;
            }
            setState({ kind: "done", already: !!res.alreadyRecorded, score });
        } catch (error) {
            // The link's token + score are validated above, so a 400 from the backend means the
            // token itself is expired/invalid (verifyCycleToken) — show the expired screen.
            if ((error as any)?.response?.status === 400) {
                setState({ kind: "expired" });
                return;
            }
            // Only backend copy is safe to show — axios puts "Network Error" in .message when the
            // request never lands, and that must not reach a partner.
            const userMsg = (error as any)?.response?.data?.userMsg;
            setState({ kind: "error", message: userMsg || "We couldn't record your feedback right now. Please try again in a moment." });
        }
    }

    return (
        <APExpanded>
            <APColumn mainAxisSize="max" style={{ height: "100svh", backgroundColor: "#f2f5f8" }}>
                {/* AssetPlus branded header — same chrome as the onboarding pages */}
                <div style={{
                    width: "100%",
                    height: "56px",
                    flexShrink: 0,
                    backgroundColor: "#1b3e78",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "8px 16px",
                }}>
                    <img src="/Assetplus_Partner.svg" alt="AssetPlus" style={{ height: "18px" }} />
                </div>

                <APColumn
                    mainAxisAlignment="center"
                    crossAxisAlignment="center"
                    mainAxisSize="max"
                    style={{ width: "100%", padding: "24px", flex: 1 }}
                >
                    {/* White card holds the content for every state (per design). */}
                    <APCard padding="32px 24px" style={{ width: "100%", maxWidth: 400 }}>
                        <APColumn crossAxisAlignment="center" gap="12px">
                            <Body state={state} />
                        </APColumn>
                    </APCard>
                </APColumn>
            </APColumn>
        </APExpanded>
    );
}

function Body({ state }: { state: State }) {
    if (state.kind === "submitting") {
        return (
            <APText variant="paragraph-large" color={APPalette["grey-500"]} center>
                Recording your feedback...
            </APText>
        );
    }

    if (state.kind === "expired") {
        return (
            <>
                <APIcon icon="clockWarning" size={64} />
                <APText variant="h2" color={APPalette["brand-400"]} center>
                    This link has expired
                </APText>
                <APText variant="paragraph-medium" color={APPalette["grey-700"]} center style={{ maxWidth: 420 }}>
                    This feedback link is no longer active.
                </APText>
            </>
        );
    }

    if (state.kind === "error") {
        return (
            <APText variant="h3" color={APPalette["negative-300"]} center style={{ maxWidth: 420 }}>
                {state.message}
            </APText>
        );
    }

    return (
        <>
            <APIcon icon="checkCircle" size={64} color={APPalette["positive-300"]} />
            {!state.already && (
                <APRow
                    mainAxisSize="min"
                    crossAxisAlignment="center"
                    gap="4px"
                    style={{ backgroundColor: APPalette["brand-100"], borderRadius: "20px", padding: "4px 12px" }}
                >
                    <APText variant="paragraph-small" color={APPalette["grey-600"]}>
                        Your rating:
                    </APText>
                    <APText variant="paragraph-small" color={APPalette["brand-400"]} style={{ fontWeight: 700 }}>
                        {formatScore(state.score)}
                    </APText>
                </APRow>
            )}
            <APText variant="h2" color={APPalette["brand-400"]} center>
                {state.already ? "Your feedback is already in" : "Thank you for your feedback"}
            </APText>
            <APText variant="paragraph-medium" color={APPalette["grey-700"]} center style={{ maxWidth: 420 }}>
                {state.already
                    ? "We've already recorded your rating for this survey."
                    : "Your rating is in. It helps us decide what to build next."}
            </APText>
        </>
    );
}
